import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import StepButton from "@mui/material/StepButton";
import { Button, FormHelperText, Step, Stepper, TextField } from "@mui/material";
import {
  FieldError,
  FieldErrorsImpl,
  FieldValues,
  FormProvider,
  Merge,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";

import AttachmentBox from "../form/AttachmentBox";
import StepperDropdown from "./StepperDropdown";
import "./stepper-form.scss";
import { DatePicker, DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { initForm } from "./stepper-form-file";
import {
  ConditionType,
  Constraint,
  ListProps,
  StepperFormField,
  StepperFormType,
  SubformFormProps,
} from "./stepper-interfaces";

type StepperFormContext = {
  initialValue: Record<string, any>;
  disableEditField: boolean;
};

const StepperFormContext = createContext<StepperFormContext>({} as StepperFormContext);

const List: React.FC<ListProps> = ({ id, label, fields, disabled }) => {
  const form = useFormContext();
  const { control } = form;
  const { append, fields: listFields, remove } = useFieldArray({ control, name: id });

  const addRow = () => append({});

  const removeRow = (index: number) => {
    remove(index);
  };

  return (
    <div className="list--container">
      <div className="header--container">
        <div>{label}</div>
        <Button variant="outlined" onClick={addRow} disabled={disabled}>
          Add
        </Button>
      </div>
      {listFields.map((value: any, index: number) => (
        <div key={index} className="flex items-center gap-2 mb-2">
          {fields
            ?.sort((a, b) => a.sequence - b.sequence)
            .map((field) => (
              <FormField key={`${id}.${index}.${field.id}`} {...field} id={`${id}.${index}.${field.id}`} />
            ))}
          {/* {errors.rows && errors.rows[index] && <span>This field is required</span>} */}
          <Button variant="outlined" onClick={() => removeRow(index)} disabled={disabled}>
            X
          </Button>
        </div>
      ))}
    </div>
  );
};

const CONDITION_MSG_MAP: Record<ConditionType, string> = {
  eq: "Equal to",
  gr: "Greater than",
  gre: "Greater than or Equal to",
  in: "Present in",
  ls: "Less than",
  lse: "Less than or Equal to",
  maxOf: "Less than",
  minOf: "Greater Than",
  ne: "Not Equal To",
};

const filterNumbers = (arr: (number | string)[]): number[] => arr.filter((v) => typeof v === "number") as number[];

const filterStrings = (arr: (number | string)[]): string[] => arr.filter((v) => typeof v === "string") as string[];

const isSingleValue = (val: number | string | (number | string)[]): val is number | string =>
  typeof val === "number" || typeof val === "string";

const isMultipleValues = (val: number | string | (number | string)[]): val is (number | string)[] => Array.isArray(val);

export const StepperWrapper = () => {
  const [FORM, _setFORM] = useState<StepperFormType>(initForm);
  return <StepperFormGenerator form={FORM} loading={false} initialValue={{}} submitForm={() => {}} />;
};

export function StepperFormViewer({
  initForm,
  initialValue,
  submitForm,
  disableEditField = true,
}: {
  initForm: StepperFormType;
  initialValue: Record<string, any>;
  submitForm: (values: any) => void;
  disableEditField?: boolean;
}) {
  const [FORM, _setFORM] = useState(initForm);

  return (
    <StepperFormContext.Provider value={{ initialValue, disableEditField }}>
      <StepperFormGenerator form={FORM} loading={false} initialValue={initialValue} submitForm={submitForm} />
    </StepperFormContext.Provider>
  );
}

function isConditionValidated(condition: any, value: any, current_value: any) {
  if (
    condition === "eq" &&
    (value === undefined || value === null) &&
    (current_value === undefined || current_value === null)
  )
    return true;

  if (value === undefined || value === null || current_value === undefined || current_value === null) return false;

  switch (condition) {
    case "eq": {
      if (isSingleValue(current_value)) {
        if (!isMultipleValues(value)) {
          if (current_value != value) return false;
        } else {
          if (!value.includes(current_value)) return false;
        }
      } else {
        if (!(current_value as (number | string)[]).some((val) => value.includes(val))) return false;
      }
      if (current_value != value) return false;
      break;
    }
    case "gre": {
      if (isSingleValue(current_value) && Number(current_value) <= Math.max(...filterNumbers(value))) return false;
      if (
        isMultipleValues(current_value) &&
        current_value.every((val) => Number(val) <= Math.max(...filterNumbers(value)))
      )
        return false;
      if (Number(current_value) < Number(value)) return false;
      break;
    }
    case "gr": {
      if(current_value && value && dayjs(current_value).isValid() && dayjs(value).isValid()){
        const isBefore = dayjs(current_value).isBefore(dayjs(value));
        if(isBefore){
          return false;
        }
        return true;
      }
      if (isSingleValue(current_value) && Number(current_value) < Math.max(...filterNumbers(value))) return false;
      if (
        isMultipleValues(current_value) &&
        current_value.every((val) => Number(val) < Math.max(...filterNumbers(value)))
      )
        return false;
      if (Number(current_value) <= Number(value)) return false;
      break;
    }
    case "lse": {
      if (isSingleValue(current_value) && Number(current_value) >= Math.min(...filterNumbers(value))) return false;
      if (
        isMultipleValues(current_value) &&
        current_value.every((val) => Number(val) >= Math.min(...filterNumbers(value)))
      )
        return false;
      if (Number(value) < Number(current_value)) return false;
      break;
    }
    case "ls": {
      // if(current_value &&  value && dayjs(current_value).isValid() && dayjs(value).isValid()){
      //   const isAfter = dayjs(current_value).isAfter(dayjs(value));
      //   if(isAfter){
      //     return false;
      //   }
      //   return true;
      // }
      if (isSingleValue(current_value) && Number(current_value) > Math.min(...filterNumbers(value))) return false;
      if (
        isMultipleValues(current_value) &&
        current_value.every((val) => Number(val) > Math.min(...filterNumbers(value)))
      )
        return false;
      if (Number(value) <= Number(current_value)) return false;
      break;
    }
    case "ne": {
      if (isSingleValue(current_value) && value.includes(current_value)) return false;
      if (isMultipleValues(current_value) && (current_value as (number | string)[]).some((val) => value.includes(val)))
        return false;
      if (value === current_value) return false;
      break;
    }
    case "in": {
      if (isSingleValue(current_value) && !value.includes(current_value)) return false;
      if (isMultipleValues(current_value) && !(current_value as (number | string)[]).some((val) => value.includes(val)))
        return false;
      break;
    }
    // case "maxOf": {
    //   if (isSingleValue(current_value) && current_value !== Math.max(...filterNumbers(value))) return false;
    //   if (
    //     isMultipleValues(current_value) &&
    //     (current_value as (number | string)[]).some((val) => val !== Math.max(...filterNumbers(value)))
    //   )
    //     return false;
    //   break;
    // }
    // case "minOf": {
    //   if (isSingleValue(current_value) && current_value !== Math.min(...filterNumbers(value))) return false;
    //   if (
    //     isMultipleValues(current_value) &&
    //     (current_value as (number | string)[]).some((val) => val !== Math.min(...filterNumbers(value)))
    //   )
    //     return false;
    //   break;
    // }
    default: {
      return true;
    }
  }
  return true;
}

function isFieldValidated(value: any, formValues: FieldValues, props: StepperFormField) {
  const { constraints, datatype, id } = props;

  // console.log("value", value, formValues, props);

  const validate_constrains: Constraint[] = [];
  // if (id === "int_test") {
  //   console.log("code working here", constrains);
  // }
  for (const constrain of constraints) {
    if (constrain.type !== "validate") continue;
    validate_constrains.push(constrain);
    const { field, condition, error_msg } = constrain;
    const parent_value = formValues[field];
    // if (parent_value === null || parent_value === undefined)
    //   return "This path may have a constrain which might not be completely traceable";

    const check_condition = isConditionValidated(condition, parent_value, value);
    if (!check_condition) {
      return error_msg || `Value must be ${condition ? CONDITION_MSG_MAP?.[condition] : condition} field ${field}`;
    }
  }

  if (value === null || value === undefined || value === "") return true;
  const { minValue, maxValue, required, maxLength, minLength } = props;
  switch (datatype) {
    case "INT": {
      const INTEGER_MATCH = /^-?\d+$/;
      if (!required && !value) return true;

      const match = String(value)?.match(INTEGER_MATCH);
      if (!match) {
        return "Allowed only Integer Values";
      }

      if (maxValue !== undefined && maxValue !== null && Number(value) > Number(maxValue)) {
        return `Input value should be less than ${maxValue}`;
      }

      if (minValue !== undefined && minValue !== null && Number(value) < Number(minValue)) {
        return `Input value should be greater than ${minValue}`;
      }

      if (minLength !== undefined && maxLength !== undefined && minLength === maxLength) {
        return maxLength === value?.length ? true : `Value length must be ${maxLength} digits`;
      } else {
        if (minLength !== undefined && minLength !== null && value?.length < minLength) {
          return `Input length should be greater than ${minLength}`;
        }

        if (maxLength !== undefined && maxLength !== null && value?.length > maxLength) {
          return `Input Length should be less than ${maxLength}`;
        }
      }
      break;
    }
    case "FLOAT": {
      if (isNaN(Number(value))) return "Not a valid Number";
      break;
    }

    case "DATE": {
      // date validation required

      if (!required && !value) return true;

      // console.log('value_valid',(value).isValid())

      if (!dayjs(value).isValid()) return "Date is not in valid format";

      break;
    }

    case "DATETIME": {
      if (!required && !value) return true;

      // if (!dayjs(value).isValid()) return "Date is not in valid format";

      break;
    }

    case "LIST":
      break;
  }

  return true;
}

function getErrorFromObject(key: string, errors: Record<string, any>) {
  const path = key.split(".");
  let current = errors;

  for (let segment of path) {
    if (segment in current) {
      current = current[segment]; // Move to the next level
    } else {
      return undefined; // Key does not exist
    }
  }

  return current; // Return the final value
}

const FormField: React.FC<StepperFormField> = (props) => {
  const form = useFormContext();
  const { register, unregister, control, setValue, formState } = form;

  const {
    label,
    constraints,
    datatype,
    id,
    range,
    hinttext = "",
    disabled = false,
    required = false,
    multiple = false,
    fields,
    isDisabledOnEdit = false,
    type = "STRING",
    maxLength
  } = props;
  const stepperFormContext = useContext(StepperFormContext);
  const { initialValue, disableEditField } = stepperFormContext;
  const isInputDisabled = Boolean(
    disabled || (Object.keys(initialValue || {}).length > 0 ? isDisabledOnEdit && disableEditField : false),
  );

  const all_values = useWatch({ control });
  const currentValue = useWatch({ control, name: id });
  const { errors } = formState;

  const error = getErrorFromObject(id, errors);

  const invalid = !!error;
  const error_message = error?.message;

  useEffect(() => {
    return () => {
      unregister(id);
    };
  }, []);

  const showwhen_constraints = useMemo(() => {
    return constraints.filter((constraint) => constraint.type === "showwhen");
  }, [constraints]);

  const is_show = useMemo(() => {
    for (const constraint of showwhen_constraints) {
      const { condition, field, value } = constraint;
      if (!condition || !field || !value) continue;
      const current_value = all_values[field];
      const check_condition = isConditionValidated(condition, value, current_value);
      if (!check_condition) return false;

      return true;
    }

    return true;
  }, [all_values, showwhen_constraints]);

  // const error_message = error?.message;
  const fieldProps: any = {};

  if (!is_show) {
    return <></>;
  }

  switch (datatype) {
    case "DATETIME":
    case "STRING":
    case "DATE":
    case "EMAIL":
    case "FLOAT":
    case "INT":
    case "URL": {
      if (datatype === "DATE") {
        fieldProps.InputLabelProps = { shrink: true };
        fieldProps.type = "date";
      }

      if (datatype === "INT") {
        fieldProps.type = "number";
      }

      if (datatype === "STRING") {
        fieldProps.type = "text";
      }

      if (datatype === "FLOAT") {
        fieldProps.type = "number";
        fieldProps.inputProps = {
          min: "0.00",
          step: "0.01",
          presicion: 2,
        };
      }

      if (range) {
        return (
          <StepperDropdown
            sx={{ flexBasis: "200px" }}
            id={id}
            options={range}
            label={`${label} ${!!required ? "*" : ""}`}
            multiple={multiple}
            disabled={isInputDisabled}
            error={invalid}
            helperText={error_message}
            constraints={constraints}
            {...form}
            {...register(id, {
              required: required ?? false,
              // value: 0,
              // min: { value: _minValue, message: `Must be greater than ${_minValue}` },
              // max: { value: _maxValue, message: `Must be lesser than ${_maxValue}` },
              validate: (value, formValues) => {
                return isFieldValidated(value, formValues, props);
              },
              // valueAsNumber: datatype === "INT" || datatype === "FLOAT",
            })}
            onChange={(value: any) => setValue(id, value)}
            // {...validation}
          />
        );
      }

      if (datatype === "DATETIME") {
        fieldProps.disableFuture = props.disableFuture;
        fieldProps.disablePast = props.disablePast;

        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              renderInput={(params) => (
                <TextField
                  size="small"
                  {...params}
                  label={`${label} ${!!required ? "*" : ""}`}
                  placeholder={`${label} ${!!required ? "*" : ""}`}
                  error={invalid}
                  helperText={error_message}
                />
              )}
              {...fieldProps}
              disabled={isInputDisabled}
              label={`${label} ${!!required ? "*" : ""}`}
              disableFuture={props.disableFuture}
              disablePast={props.disablePast}
              slotProps={{
                textField: {
                  required: required ?? false,
                },
              }}
              value={currentValue || null}
              // {...form}
              {...register(id, {
                required: required ?? false,
                // value: 0,
                // min: { value: _minValue, message: `Must be greater than ${_minValue}` },
                // max: { value: _maxValue, message: `Must be lesser than ${_maxValue}` },
                validate: (value, formValues) => {
                  return isFieldValidated(value, formValues, props);
                },
                // valueAsNumber: datatype === "INT" || datatype === "FLOAT",
              })}
              onChange={(value) => setValue(id, value)}
            />
          </LocalizationProvider>
        );
      }

      if (datatype === "DATE") {
        fieldProps.disableFuture = props.disableFuture;
        fieldProps.disablePast = props.disablePast;
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`${label} ${!!required ? "*" : ""}`}
                  placeholder={`${label} ${!!required ? "*" : ""}`}
                  error={invalid}
                  helperText={error_message}
                  size="small"
                />
              )}
              label={`${label} ${!!required ? "*" : ""}`}
              disabled={isInputDisabled}
              {...fieldProps}
              inputFormat="DD/MM/YYYY"
              value={currentValue || null}
              {...register(id, {
                required: required ?? false,
                // value: 0,
                // min: { value: _minValue, message: `Must be greater than ${_minValue}` },
                // max: { value: _maxValue, message: `Must be lesser than ${_maxValue}` },
                validate: (value, formValues) => {
                  // console.dir({ value, formValues });

                  return isFieldValidated(value, formValues, props);
                },
                // valueAsNumber: datatype === "INT" || datatype === "FLOAT",
              })}
              onChange={(value) => setValue(id, value)}
            />
          </LocalizationProvider>
        );
      }

      return (
        <>
          {/* <FormControl fullWidth> */}
          <TextField
            className="field"
            type={"text"}
            placeholder={hinttext}
            // label={label}
            disabled={isInputDisabled}
            error={invalid}
            helperText={error_message}
            {...fieldProps}
            {...register(id, {
              required: required ?? false,
              // value: 0,
              // min: { value: _minValue, message: `Must be greater than ${_minValue}` },
              // max: { value: _maxValue, message: `Must be lesser than ${_maxValue}` },
              validate: (value, formValues) => {
                return isFieldValidated(value, formValues, props);
              },
              // valueAsNumber: datatype === "INT" || datatype === "FLOAT",
            })}
            onChange={(e) => {
              if (maxLength && maxLength > 0) {
                if (String(e.target.value).length <= maxLength) {
                  setValue(id, e.target.value);
                }
              } else {
                setValue(id, e.target.value);
              }
            }}
            size="small"
            value={currentValue ?? ""}
            label={`${label} ${!!required ? "*" : ""}`}
          />
          {/* <FieldErrorMessage error={errors[id]} /> */}
          {/* </FormControl> */}
        </>
      );
    }

    case "DERIVED":
      const value = props.deriveValue(all_values);
      const helperText = props?.helperText?.(all_values) || "";
      if (type === "DATE") {
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`${label} ${!!required ? "*" : ""}`}
                  placeholder={label}
                  error={invalid}
                  helperText={helperText}
                  size="small"
                />
              )}
              label={label}
              disabled={isInputDisabled}
              {...fieldProps}
              inputFormat="DD/MM/YYYY"
              value={currentValue || null}
              slotProps={{
                textField: {
                  required: required ?? false,
                },
              }}
              {...register(id, {
                required: required ?? false,
                // value: 0,
                // min: { value: _minValue, message: `Must be greater than ${_minValue}` },
                // max: { value: _maxValue, message: `Must be lesser than ${_maxValue}` },
                validate: (value, formValues) => {
                  // console.dir({ value, formValues });

                  return isFieldValidated(value, formValues, props);
                },
                // valueAsNumber: datatype === "INT" || datatype === "FLOAT",
              })}
              onChange={(value) => setValue(id, value)}
            />
          </LocalizationProvider>
        );
      }
      return (
        <TextField
          className="field"
          type={"text"}
          placeholder={hinttext}
          // label={label}
          {...fieldProps}
          size="small"
          value={value ?? ""}
          label={`${hinttext} ${!!required ? "*" : ""}`}
          readOnly={disabled}
          helperText={helperText}
          required={required}
        />
      );
    case "LIST":
      return (
        <List
          fields={fields}
          disabled={isInputDisabled}
          id={id}
          required={required}
          label={label}
          constraints={constraints}
        />
      );
    case "ATTACHMENT":
      return <AttachmentBox id={id} required={required} {...form} />;
  }

  return <></>;
};

const FieldErrorMessage: React.FC<{ error: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined }> = ({
  error,
}) => {
  if (error?.message) return <FormHelperText error>{error.message}</FormHelperText>;
  switch (error?.type) {
    case "required":
      return <FormHelperText error>This field is required</FormHelperText>;
    default:
      return <>{}</>;
  }
};

const SubformFormGenerator: React.FC<SubformFormProps> = (props) => {
  const {
    defaultValues,
    subFormSubmit,
    form,
    current_index = 0,
    last_index = 0,
    handleBackNavigation,
    loading,
  } = props;
  const formMethods = useForm({ mode: "onChange", defaultValues, reValidateMode: "onChange" });
  const { handleSubmit } = formMethods;

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(subFormSubmit)}>
          <div className="assets__asset-register__form gap-2">
            {form.fields
              ?.sort((a, b) => a.sequence - b.sequence)
              .map((field) => (
                <FormField key={field.id} {...field} />
              ))}
          </div>
          <div className="flex justify-end gap-2" style={{ marginTop: "1rem" }}>
            {current_index !== 0 && (
              <Button variant="outlined" type="button" onClick={handleBackNavigation}>
                Back
              </Button>
            )}
            <Button type="submit" variant="contained">
              {current_index !== last_index ? "Next" : "Submit"}
            </Button>
          </div>
        </form>
      </FormProvider>
    </>
  );
};

// interface SubformStepperProps {
//   sub_form: SubformType;
//   handleChange: (sub_form: SubformType) => void;
//   selected: boolean;
// }

// const SubformStepper: React.FC<SubformStepperProps> = (props) => {
//   const { sub_form, handleChange, selected } = props;

//   return (
//     <Button onClick={() => handleChange(sub_form)} variant={selected ? "contained" : "outlined"}>
//       {sub_form.title}
//     </Button>

//   );
// };

interface Props {
  form: StepperFormType;
  loading: boolean;
  initialValue: Record<string, any>;
  submitForm: (values: any) => void;
  //   default_selected_subform: Subform
}

const defaultVal = {
  int_test: "4124",
  new_int: "421",
  test: ["MALE"],
  test2: "1",
  new_int_child: "",
  "test-subform-2": ["MALE"],
};
const StepperFormGenerator: React.FC<Props> = (props) => {
  const { form, loading, initialValue = {}, submitForm } = props;
  //   const default_selected_subform = form?.subforms?.[0];
  const [state, setState] = useState({ selected_subform_index: 0 });
  const subform_list = form.subforms;
  const last_step_index = useMemo(() => subform_list.length - 1, [subform_list]);
  const [values, setValues] = useState<any>(initialValue);

  //   function handleSubFormChange(sub_form: SubformType) {
  //     setState((state) => ({ ...state, selected_subform: sub_form }));
  //   }

  function handleStep(index: number) {
    setState((state) => ({ ...state, selected_subform_index: index }));
    // return undefined;
  }

  function handleSubFormSubmit(formValues: FieldValues) {
    setValues((state: any) => ({ ...state, ...formValues }));
    if (state.selected_subform_index === last_step_index) {
      submitForm({ ...values, ...formValues });
    }
    if (state.selected_subform_index !== last_step_index) {
      setState((state) => ({ ...state, selected_subform_index: state.selected_subform_index + 1 }));
    }
  }

  function handleBackNavigation() {
    if (state.selected_subform_index !== 0) {
      setState((state) => ({
        ...state,
        selected_subform_index: state.selected_subform_index - 1,
      }));
    }
  }

  return (
    <div>
      {/* {subform_list.map((sub_form) => (
          <SubformStepper
            selected={sub_form.id === state.selected_subform.id}
            sub_form={sub_form}
            handleChange={handleSubFormChange}
          />

        ))} */}
      <Stepper activeStep={state.selected_subform_index} sx={{ width: "100%", marginBottom: "1rem" }}>
        {subform_list
          .sort((a, b) => a.sequence - b.sequence)
          .map((sub_form, index) => (
            <Step key={sub_form.id} completed={false}>
              <StepButton color="inherit" onClick={() => handleStep(index)}>
                {sub_form.title}
              </StepButton>
            </Step>
          ))}
      </Stepper>
      {subform_list.map(
        (subform, index) =>
          index === state.selected_subform_index && (
            <SubformFormGenerator
              key={subform.id}
              current_index={state.selected_subform_index}
              last_index={last_step_index}
              form={subform}
              defaultValues={values}
              subFormSubmit={handleSubFormSubmit}
              handleBackNavigation={handleBackNavigation}
              loading={loading}
            />
          ),
      )}
      {/* <SubformFormGenerator
        form={form?.subforms[state.selected_subform_index]}
        defaultValues={values}
        subFormSubmit={handleSubFormSubmit}
      /> */}
    </div>
  );
};

export default StepperWrapper;
